import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { FaInstagram, FaCheck } from "react-icons/fa";
import { MdModeEditOutline } from "react-icons/md";
import { IoFootballOutline, IoImagesOutline } from "react-icons/io5";

interface ProfileData {
  profile_text?: string | null;
  profile_interests?: string[] | null;
  profile_images?: string[];
  instagram_handle?: string | null;
}

interface CompleteProfileListProps {
  profileData: ProfileData;
}

const CompleteProfileList: React.FC<CompleteProfileListProps> = ({
  profileData,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const colors = useColors();

  const items = [
    {
      id: "profileText",
      title: t("profile_tab.complete_profile.profile_text_title") || "Bio",
      subtitle:
        t("profile_tab.complete_profile.profile_text") ||
        "Tell others about yourself",
      icon: (
        <MdModeEditOutline
          size={24}
          style={{ color: colors.textNeutralDefault }}
        />
      ),
      completed:
        !!profileData.profile_text && profileData.profile_text.length > 0,
    },
    {
      id: "hobbies",
      title: t("profile_tab.complete_profile.hobbies_title") || "Interests",
      subtitle: t("profile_tab.complete_profile.hobbies") || "Add your hobbies",
      icon: (
        <IoFootballOutline
          size={24}
          style={{ color: colors.textNeutralDefault }}
        />
      ),
      completed:
        !!profileData.profile_interests &&
        profileData.profile_interests.length > 0,
    },
    {
      id: "images",
      title: t("profile_tab.complete_profile.photos_title") || "Photos",
      subtitle:
        t("profile_tab.complete_profile.photos") || "Add at least 3 photos",
      icon: (
        <IoImagesOutline
          size={24}
          style={{ color: colors.textNeutralDefault }}
        />
      ),
      completed:
        !!profileData.profile_images && profileData.profile_images.length >= 3,
    },
    {
      id: "instagram_handle",
      title:
        t("profile_tab.complete_profile.instagram_handle_title") || "Instagram",
      subtitle:
        t("profile_tab.complete_profile.instagram_handle_subtitle") ||
        "Link your Instagram",
      icon: (
        <FaInstagram size={24} style={{ color: colors.textNeutralDefault }} />
      ),
      completed:
        !!profileData.instagram_handle &&
        profileData.instagram_handle.length > 0,
    },
  ].sort((a, b) => (a.completed === b.completed ? 0 : a.completed ? 1 : -1));

  const completedCount = items.filter((item) => item.completed).length;

  // Hide if all items are completed
  if (completedCount === items.length) {
    return null;
  }

  const handleItemClick = () => {
    navigate("/profile/edit");
  };

  return (
    <div className="mb-6 w-full max-w-[100vw] md:max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-4 sm:px-6">
        <h3
          className="text-base font-semibold"
          style={{ color: colors.textNeutralDefault }}
        >
          {t("profile_tab.complete_profile.title") || "Complete your profile"}
        </h3>
        <span className="text-sm" style={{ color: colors.accentColor }}>
          {completedCount}/{items.length}{" "}
          {t("profile_tab.complete_profile.done") || "done"}
        </span>
      </div>

      {/* Horizontal scrolling on mobile, grid on desktop */}
      <div className="flex gap-3 overflow-x-auto pb-2 px-4 sm:px-6 md:px-0 scrollbar-hide md:grid md:grid-cols-4 md:overflow-visible">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={handleItemClick}
            className="flex-shrink-0 w-44 sm:w-48 md:w-full p-4 rounded-2xl text-center transition-opacity hover:opacity-80"
            style={{ backgroundColor: colors.containerNeutralLightLowest }}
          >
            {/* Icon */}
            <div
              className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
              style={{ backgroundColor: colors.containerNeutralLightMedium }}
            >
              {item.completed ? (
                <FaCheck
                  size={20}
                  style={{ color: colors.textNeutralDefault }}
                />
              ) : (
                React.cloneElement(item.icon, { size: 24 })
              )}
            </div>

            {/* Title */}
            <p
              className="font-semibold text-sm mb-0.5"
              style={{ color: colors.textNeutralDefault }}
            >
              {item.title}
            </p>

            {/* Subtitle */}
            <p
              className="text-xs mb-3"
              style={{ color: colors.textNeutralMuted }}
            >
              {item.subtitle}
            </p>

            {/* Action Button */}
            <div
              className="py-1.5 px-3 rounded-lg text-xs font-semibold"
              style={{
                backgroundColor: item.completed
                  ? "transparent"
                  : colors.containerNeutralSolidSurfaceInverted ||
                    colors.textNeutralDefault,
                color: item.completed
                  ? colors.textNeutralMuted
                  : colors.textNeutralDefaultInverted ||
                    colors.containerNeutralSolidSurface,
                border: item.completed
                  ? `1px solid ${colors.containerNeutralLightMedium}`
                  : "none",
                opacity: item.completed ? 0.6 : 1,
              }}
            >
              {item.completed
                ? t("profile_tab.complete_profile.done") || "Done"
                : t("profile_tab.complete_profile.add") || "Add"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CompleteProfileList;
